// A list of nested records on a form (lanes, participants, custom fields,
// rules, an option field's options): add rows from <template>s, remove them,
// and drag them into order.
//
// Each <template> holds a new row whose field names use the template's
// placeholder child index (data-nested-list-placeholder); adding a row swaps it
// for a unique index, as Rails' nested attributes expect. Removing a saved row
// hides it and sets its [data-nested-list-removal] field (`archived`, or
// `_destroy`), so the server archives it; a new row is simply dropped. When the
// list is sortable, each row's [data-nested-list-position] field is kept equal
// to its place in the list.
//
// Midwest's repeatable does most of this, but it removes saved rows from the
// page (so they are never archived), clones the first row rather than a
// template, and renumbers every index in a nested list's names.
import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class NestedListController extends Controller<HTMLElement> {
  static override targets = ["list", "template", "item", "empty"];
  static override values = { sortable: Boolean };

  declare readonly listTarget: HTMLElement;
  declare readonly templateTargets: HTMLTemplateElement[];
  declare readonly itemTargets: HTMLElement[];
  declare readonly hasEmptyTarget: boolean;
  declare readonly emptyTarget: HTMLElement;
  declare readonly sortableValue: boolean;

  private sortable?: Sortable;

  override connect(): void {
    if (this.sortableValue) {
      this.sortable = Sortable.create(this.listTarget, {
        animation: 150,
        handle: "[data-nested-list-handle]",
        draggable: "[data-nested-list-target~='item']",
        onEnd: () => this.refresh(),
      });
    }
    this.refresh();
  }

  override disconnect(): void {
    this.sortable?.destroy();
  }

  add(event: Event & { params?: { template?: string } }): void {
    event.preventDefault();

    const name = event.params?.template;
    const template = this.templateTargets.find((candidate) => !name || candidate.dataset.name === name);
    if (!template) return;

    const placeholder = template.dataset.nestedListPlaceholder ?? "";
    const index = `${Date.now()}${Math.floor(Math.random() * 1000)}`;
    const holder = document.createElement("div");
    holder.innerHTML = template.innerHTML.replaceAll(placeholder, index);

    // Chosen from a menu (a custom field's type): close it.
    (event.target as Element).closest<HTMLElement>(":popover-open")?.hidePopover();

    const items = Array.from(holder.children);
    this.listTarget.append(...items);
    this.refresh();
    items[0]?.querySelector<HTMLElement>("input:not([type='hidden']), textarea, select")?.focus();
  }

  remove(event: Event): void {
    event.preventDefault();

    const item = (event.target as Element).closest<HTMLElement>("[data-nested-list-target~='item']");
    if (!item || !this.itemTargets.includes(item)) return;

    const removals = this.ownFields(item, "[data-nested-list-removal]");
    if (removals.length && item.dataset.persisted === "true") {
      removals.forEach((field) => (field.value = field.dataset.nestedListRemoval || "1"));
      // A hidden row can't be corrected, so it mustn't block the form.
      item.querySelectorAll<HTMLInputElement>("[required]").forEach((field) => (field.required = false));
      item.querySelectorAll<HTMLElement>(":popover-open").forEach((popover) => popover.hidePopover());
      item.hidden = true;
      item.style.display = "none";
      item.dataset.removed = "true";
    } else {
      item.remove();
    }
    this.refresh();
  }

  private refresh(): void {
    const visible = this.itemTargets.filter((item) => item.dataset.removed !== "true");

    visible.forEach((item, position) => {
      this.ownFields(item, "[data-nested-list-position]").forEach((field) => (field.value = String(position)));
    });

    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible.length > 0;
  }

  // The fields of this row itself, not of a list nested inside it.
  private ownFields(item: HTMLElement, selector: string): HTMLInputElement[] {
    return Array.from(item.querySelectorAll<HTMLInputElement>(selector)).filter(
      (field) => field.closest("[data-nested-list-target~='item']") === item,
    );
  }
}
